import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CheckoutFooter from "../components/footer";
import AppConfig from "../../appConfig";
import axios from "../../axios";
import usePageTitle from "../pageTitle";
import {
  // Container,
  Box,
  Backdrop,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
  Paper,
  Card,
  Icon,
  Grid,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";

import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { usePDF, Resolution, Margin } from "react-to-pdf";
import GoogleMapReact from "google-map-react";
import Grid2 from "@mui/material/Unstable_Grid2";

function CampaignWindowPage() {
  usePageTitle("Campaign Window");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [reportName, setReportName] = useState("reports_overview.pdf");
  const options = {
    filename: reportName,
    // default is `save`
    // method: "open",
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    // resolution: Resolution.HIGH,
    page: {
      // margin is in MM, default is Margin.NONE = 0
      // margin: Margin.SMALL,
      // default is 'A4'
      // format: "letter",
      // default is 'portrait'
      // orientation: "landscape",
    },
    // canvas: {
    //   // default is 'image/jpeg' for better size performance
    //   mimeType: "image/png",
    //   qualityRatio: 1,
    // },
    // // Customize any value passed to the jsPDF instance and html2canvas
    // // function. You probably will not need this and things can break,
    // // so use with caution.
    overrides: {
      // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
      pdf: {
        compress: true,
      },
      // see https://html2canvas.hertzen.com/configuration for more options
      canvas: {
        // useCORS: true,
        imageTimeout: 10000,
      },
    },
  };
  const { toPDF, targetRef } = usePDF(options);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [overview, setOverview] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [agents, setAgents] = useState(null);
  const [salesMetrics, setSalesMetrics] = useState(null);
  const [dataMetrics, setDataMetrics] = useState(null);
  const [groupMetrics, setGroupMetrics] = useState(null);
  const [supervisorMetrics, setSupervisorMetrics] = useState(null);
  const [locationPoints, setLocationPoints] = useState([]);
  const [mapZoom, setMapZoom] = useState(6);

  useEffect(() => {
    if (ref === null) {
      navigate("/error");
      return;
    }

    setShowLoading(true);
    const fetchData = async () => {
      await axios
        .get(`campaign/window/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            console.log(result.data.data);
            setCampaign(result.data.data.campaign);
            setOverview(result.data.data.overview);
            setAgents(result.data.data.agents);
            setSalesMetrics(result.data.data.salesMetrics);
            setDataMetrics(result.data.data.dataMetrics);
            setGroupMetrics(result.data.data.groupMetrics);
            setSupervisorMetrics(result.data.data.supervisorMetrics);
            setLocationPoints(result.data.data.overview.locationPoints);
            setCurrency(result.data.data.currency);
            setReportName(
              `${result.data.data.campaign.account.title}_${result.data.data.campaign.title}_report.pdf`
            );

            // Handle App Update
            const currentVersion = localStorage.getItem(
              AppConfig.app_version_store
            );
            const newVersion = result.data.version;
            if (currentVersion !== newVersion) {
              localStorage.setItem(AppConfig.app_version_store, newVersion);
              if (
                window.confirm("New version available. Reloading...") === true
              ) {
                window.location.reload(true);
              } else {
                window.location.reload(true);
              }
              return;
            }
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
          setShowLoading(false);
        });
    };

    fetchData();
  }, [refreshPage]);

  function getChartDataSet(data) {
    let series = [];
    for (const i of data) {
      // console.log(data[i]);
      series.push({ data: [i] });
    }
    return series;
  }

  return (
    <Box>
      <Box mb={0}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={showLoading}
          onClick={handleClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleClose}
            severity={severity}
            sx={{ width: "100%" }}
          >
            {errorMsg}
          </Alert>
        </Snackbar>
      </Box>
      <Box sx={{ width: "85%", margin: "0px auto" }} ref={targetRef}>
        <Box mb={5}>
          {campaign && (
            <Box mx={1}>
              <Box>
                <Paper elevation={0}>
                  <Box p={2} align="center">
                    <Box mt={0} display="flex" justifyContent="space-between">
                      <Box>
                        <Typography variant="h6" fontWeight={400}>
                          <Icon color="info">bar_chart</Icon>
                        </Typography>
                      </Box>
                      <Box>
                        <Button
                          onClick={() => {
                            setRefreshPage(!refreshPage);
                          }}
                          startIcon={<Icon>replay</Icon>}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Box>
                    <Box mt={2}>
                      <img
                        src={`${AppConfig.api_media_url}${campaign.account.logo}`}
                        alt="img"
                        style={{ maxWidth: 200, maxHeight: "80%" }}
                      />
                    </Box>
                    <Box mt={1}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {campaign.account.title}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="caption" fontWeight={350}>
                        {campaign.title}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
              <Box mt={3}>
                <Box>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      md={campaign.campaign_type === "DATA" ? 6 : 8}
                      lg={campaign.campaign_type === "DATA" ? 6 : 8}
                    >
                      {campaign.campaign_type === "DATA" ? (
                        <Card sx={{ height: "100%", marginTop: "0px" }}>
                          <Box p={3}>
                            <Box align="center">
                              <Icon color="info" fontSize="large">
                                edit_note
                              </Icon>
                              <Typography variant="h6" fontWeight="light">
                                Data Captures
                              </Typography>
                            </Box>
                            <Box mt={5}>
                              <Grid container spacing={3}>
                                <Grid item xs={6} md={6} lg={6}>
                                  <Box
                                    sx={{
                                      height: "100%",
                                      marginTop: "0px",
                                      // display: "flex",
                                      textAlign: {
                                        xs: "center",
                                        md: "start",
                                        lg: "start",
                                      },
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      fontWeight="medium"
                                    >
                                      {overview.total.toLocaleString("en", {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                      })}
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      fontWeight="light"
                                      fontSize={14}
                                    >
                                      Total
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={6} md={6} lg={6}>
                                  <Box
                                    sx={{
                                      textAlign: {
                                        xs: "center",
                                        md: "end",
                                        lg: "end",
                                      },
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      fontWeight="medium"
                                    >
                                      {overview.today.toLocaleString("en", {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                      })}
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      fontWeight="light"
                                      fontSize={14}
                                    >
                                      Today
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </Card>
                      ) : (
                        <Box>
                          <Card sx={{ height: "100%", marginTop: "0px" }}>
                            <Box p={3}>
                              <Box align="center">
                                <Icon color="success" fontSize="large">
                                  bar_chart
                                </Icon>
                              </Box>
                              <Box mt={1}>
                                <Box align="center">
                                  <Typography
                                    variant="h6"
                                    fontWeight="medium"
                                    fontSize="32px"
                                  >
                                    {currency}
                                    {(
                                      (overview.salesVolume
                                        ? overview.salesVolume
                                        : 0) / 100
                                    ).toLocaleString("en", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    fontWeight="light"
                                    fontSize={14}
                                  >
                                    Total Volume
                                  </Typography>
                                </Box>
                              </Box>
                              <Box mt={3}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={4} lg={4}>
                                    <Box
                                      sx={{
                                        height: "100%",
                                        marginTop: "0px",
                                        // display: "flex",
                                        textAlign: {
                                          xs: "center",
                                          md: "start",
                                          lg: "start",
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        fontWeight="medium"
                                      >
                                        {(overview.salesCount
                                          ? overview.salesCount
                                          : 0
                                        ).toLocaleString("en", {
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        })}
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        fontWeight="light"
                                        fontSize={14}
                                      >
                                        Total Sales
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={4} lg={4}>
                                    <Box align="center">
                                      <Typography
                                        variant="h6"
                                        fontWeight="medium"
                                      >
                                        {currency}
                                        {(
                                          (overview.todayVolume
                                            ? overview.todayVolume
                                            : 0) / 100
                                        ).toLocaleString("en", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        fontWeight="light"
                                        fontSize={14}
                                      >
                                        Today&apos;s Volume
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={4} lg={4}>
                                    <Box
                                      sx={{
                                        textAlign: {
                                          xs: "center",
                                          md: "end",
                                          lg: "end",
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        fontWeight="medium"
                                      >
                                        {(overview.todayCount
                                          ? overview.todayCount
                                          : 0
                                        ).toLocaleString("en", {
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        })}
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        fontWeight="light"
                                        fontSize={14}
                                      >
                                        Today&apos;s Sales
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Box>
                          </Card>
                        </Box>
                      )}
                    </Grid>
                    {campaign.campaign_type === "DATA" && (
                      <Grid item xs={12} md={3} lg={3}>
                        <Card sx={{ height: "100%", marginTop: "0px" }}>
                          <Box p={3} align="center">
                            <Box>
                              <Icon color="success" fontSize="large">
                                payment
                              </Icon>
                            </Box>
                            <Box mt={0}>
                              <Typography variant="h6" fontWeight="light">
                                {overview.currency}
                              </Typography>
                            </Box>
                            <Box mt={5}>
                              <Typography variant="h6" fontWeight="medium">
                                {(overview.total_amount / 100).toLocaleString(
                                  "en",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="light"
                                fontSize={14}
                              >
                                Total Collected
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      md={campaign.campaign_type === "DATA" ? 3 : 4}
                      lg={campaign.campaign_type === "DATA" ? 3 : 4}
                    >
                      <Box sx={{ height: "100%" }}>
                        <Card sx={{ height: "100%", marginTop: "0px" }}>
                          <Box p={3} align="center">
                            <Box>
                              <Icon color="warning" fontSize="large">
                                support_agent
                              </Icon>
                            </Box>
                            <Box mt={0}>
                              <Typography variant="h6" fontWeight="light">
                                Agents
                              </Typography>
                            </Box>
                            <Box mt={5}>
                              <Typography variant="h6" fontWeight="medium">
                                {agents.total} / {agents.active}
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="light"
                                fontSize={14}
                              >
                                Total / Active
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Box mt={5}>
                <Card>
                  <Box>
                    <Box sx={{ overflow: "auto" }}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "table",
                          tableLayout: "fixed",
                        }}
                      >
                        <Box m={3} align="left">
                          <Typography>
                            {campaign.campaign_type === "DATA"
                              ? "Data Captures"
                              : "Transactions"}
                          </Typography>
                        </Box>
                        <Box align="center">
                          <BarChart
                            xAxis={[
                              {
                                scaleType: "band",
                                data: overview.breakdown.days,
                              },
                            ]}
                            // series={getChartDataSet(overview.breakdown.count)}
                            series={[{ data: overview.breakdown.count }]}
                            width={750}
                            height={300}
                            colors={["darkblue"]}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>
              {campaign.campaign_type === "SALES" && (
                <Box mt={5}>
                  <Card>
                    <Box>
                      <Box sx={{ overflow: "auto" }}>
                        <Box
                          sx={{
                            width: "100%",
                            display: "table",
                            tableLayout: "fixed",
                          }}
                        >
                          <Box m={3} align="left">
                            <Typography>Volume</Typography>
                          </Box>
                          <Box align="center">
                            <BarChart
                              xAxis={[
                                {
                                  scaleType: "band",
                                  data: overview.breakdown.days,
                                },
                              ]}
                              // series={getChartDataSet(overview.breakdown.count)}
                              series={[{ data: overview.breakdown.sum }]}
                              width={750}
                              height={300}
                              colors={["green"]}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              )}

              {salesMetrics && (
                <Box mt={5}>
                  <Box>
                    <Card>
                      <Box sx={{ overflow: "auto" }}>
                        <Box>
                          <Box m={3} align="left">
                            <Typography>Product Performance</Typography>
                          </Box>
                          <Box align="left" p={3}>
                            <PieChart
                              series={[
                                {
                                  data: salesMetrics,
                                },
                              ]}
                              paddingAngle={5}
                              height={240}
                              slotProps={{
                                legend: {
                                  hidden: true,
                                  // sx: {
                                  //   fontSize: 8, // Adjust the font size as needed
                                  // },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                  <Box mt={5}>
                    <Card sx={{ height: "100%" }}>
                      <Box p={2}>
                        <Table sx={{ width: "100%" }} aria-label="simple table">
                          <TableBody>
                            <TableRow
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="subtitle2"
                                  fontSize={14}
                                  fontWeight="bold"
                                >
                                  Product
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="subtitle2"
                                  fontSize={14}
                                  fontWeight="bold"
                                >
                                  Count
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="subtitle2"
                                  fontSize={14}
                                  fontWeight="bold"
                                >
                                  Volume
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {salesMetrics.map((product) => (
                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell>
                                  <Typography variant="subtitle2" fontSize={14}>
                                    {product.label}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" fontSize={14}>
                                    {product.value.toLocaleString("en", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="subtitle2"
                                    fontSize={14}
                                  >{`${currency}${(
                                    product.volume / 100
                                  ).toLocaleString("en", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Card>
                  </Box>
                </Box>
              )}

              {groupMetrics && (
                <Box mt={5}>
                  <Card>
                    <Box>
                      <Box sx={{ overflow: "auto" }}>
                        <Box>
                          <Box m={3} align="left">
                            <Typography>Team Performance</Typography>
                          </Box>
                          <Box align="left" p={3}>
                            <PieChart
                              series={[
                                {
                                  data: groupMetrics,
                                },
                              ]}
                              paddingAngle={10}
                              height={200}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              )}

              {supervisorMetrics && (
                <Box mt={5}>
                  <Grid2 container spacing={3}>
                    <Grid2 item xs={12} md={6} lg={6}>
                      <Card>
                        <Box>
                          <Box sx={{ overflow: "auto" }}>
                            <Box>
                              <Box m={3} align="left">
                                <Typography>Supervisors (Count)</Typography>
                              </Box>
                              <Box align="left" p={3}>
                                <PieChart
                                  series={[
                                    {
                                      data: supervisorMetrics.count,
                                    },
                                  ]}
                                  paddingAngle={10}
                                  height={200}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Card>
                    </Grid2>
                    <Grid2 item xs={12} md={6} lg={6}>
                      <Card>
                        <Box>
                          <Box sx={{ overflow: "auto" }}>
                            <Box>
                              <Box m={3} align="left">
                                <Typography>Supervisors (Volume)</Typography>
                              </Box>
                              <Box align="left" p={3}>
                                <PieChart
                                  series={[
                                    {
                                      data: supervisorMetrics.vol,
                                    },
                                  ]}
                                  paddingAngle={10}
                                  height={200}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Card>
                    </Grid2>
                  </Grid2>
                </Box>
              )}

              {locationPoints.length > 0 && (
                <Box mt={5}>
                  <Card>
                    <Box p={3}>
                      <Box mb={3} display="flex" justifyContent="space-between">
                        <Box>
                          <Typography>Location HeatMap</Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              let pZoom = mapZoom;
                              pZoom -= 1;
                              pZoom = pZoom < 4 ? 4 : pZoom;
                              setMapZoom(pZoom);
                            }}
                          >
                            <Icon>remove</Icon>
                          </IconButton>
                          &nbsp;
                          <Typography
                            variant="caption"
                            fontSize={10}
                            fontWeight="light"
                          >
                            Zoom
                          </Typography>
                          &nbsp;
                          <IconButton
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              let pZoom = mapZoom;
                              pZoom += 1;
                              pZoom = pZoom > 20 ? 20 : pZoom;
                              setMapZoom(pZoom);
                            }}
                          >
                            <Icon>add</Icon>
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ height: "65vh", width: "100%" }}>
                        <GoogleMapReact
                          bootstrapURLKeys={{ key: AppConfig.google_map_key }}
                          defaultCenter={{
                            lat: locationPoints[0].lat,
                            lng: locationPoints[0].lng,
                          }}
                          defaultZoom={mapZoom}
                          zoom={mapZoom}
                        >
                          {locationPoints.map((loc) => {
                            return (
                              <Box lat={loc.lat} lng={loc.lng}>
                                <Typography fontSize={24}>
                                  <Icon color="error">location_on</Icon>
                                </Typography>
                              </Box>
                            );
                          })}
                        </GoogleMapReact>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              )}

              {dataMetrics && (
                <Box mt={7}>
                  <Box align="center">
                    <Typography fontSize={18}>Data Capture Insights</Typography>
                  </Box>
                  <Box mt={1}>
                    {dataMetrics.chartData && (
                      <Box mt={3}>
                        <Grid container spacing={3}>
                          {dataMetrics.chartData.map((metric) => (
                            <Grid item xs={12} md={6} lg={6}>
                              <Card>
                                <Box>
                                  <Box sx={{ overflow: "auto" }}>
                                    <Box>
                                      <Box m={3} align="left">
                                        <Typography fontSize={12}>
                                          {metric.field.title}
                                        </Typography>
                                        <Typography>
                                          {metric.field.section}
                                        </Typography>
                                      </Box>
                                      <Box align="center" p={3}>
                                        <PieChart
                                          series={[
                                            {
                                              data: metric.metrics,
                                            },
                                          ]}
                                          paddingAngle={10}
                                          height={200}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                    {dataMetrics.spreadData && (
                      <Box mt={5}>
                        {dataMetrics.spreadData.map((item) => (
                          <Box mt={3} mb={5}>
                            <Card>
                              <Box p={3}>
                                <Box
                                  mb={3}
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <Box>
                                    <Typography fontSize={12}>
                                      {item.title}
                                    </Typography>
                                    <Typography fontSize={12}>
                                      {item.section}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ height: "57vh", width: "100%" }}>
                                  <GoogleMapReact
                                    bootstrapURLKeys={{
                                      key: AppConfig.google_map_key,
                                    }}
                                    defaultCenter={{
                                      lat: item.coords[0].lat, //9.084576,
                                      lng: item.coords[0].lng, //8.674252,
                                    }}
                                    defaultZoom={mapZoom}
                                    zoom={mapZoom}
                                  >
                                    {item.coords.map((loc) => (
                                      <Box lat={loc.lat} lng={loc.lng}>
                                        <Typography fontSize={24}>
                                          <Icon color="error">location_on</Icon>
                                        </Typography>
                                      </Box>
                                    ))}
                                  </GoogleMapReact>
                                </Box>
                              </Box>
                            </Card>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {dataMetrics.averageData && (
                      <Box mt={5}>
                        <Grid container spacing={3}>
                          {dataMetrics.averageData.map((item) => (
                            <Grid item xs={12} md={6} lg={6}>
                              <Card>
                                <Box p={3}>
                                  <Box
                                    mb={3}
                                    display="flex"
                                    justifyContent="space-between"
                                  >
                                    <Box>
                                      <Typography fontSize={12}>
                                        {item.title}
                                      </Typography>
                                      <Typography>{item.section}</Typography>
                                    </Box>
                                  </Box>
                                  <Box>
                                    <Grid container spacing={3}>
                                      <Grid item xs={6} md={6} lg={6}>
                                        <Box align="center">
                                          <Box>
                                            <Typography
                                              variant="h6"
                                              fontWeight="regular"
                                              fontSize={20}
                                            >
                                              {item.figures.sum.toLocaleString(
                                                "en",
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                }
                                              )}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography fontWeight="light">
                                              Sum
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={6} md={6} lg={6}>
                                        <Box align="center">
                                          <Box>
                                            <Typography
                                              variant="h6"
                                              fontWeight="regular"
                                              fontSize={20}
                                            >
                                              {item.figures.avg.toLocaleString(
                                                "en",
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                }
                                              )}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography fontWeight="light">
                                              Avg
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={6} md={6} lg={6}>
                                        <Box align="center">
                                          <Box>
                                            <Typography
                                              variant="h6"
                                              fontWeight="regular"
                                              fontSize={20}
                                            >
                                              {item.figures.max.toLocaleString(
                                                "en",
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                }
                                              )}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography fontWeight="light">
                                              Max
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={6} md={6} lg={6}>
                                        <Box align="center">
                                          <Box>
                                            <Typography
                                              variant="h6"
                                              fontWeight="regular"
                                              fontSize={20}
                                            >
                                              {item.figures.min.toLocaleString(
                                                "en",
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                }
                                              )}
                                            </Typography>
                                          </Box>
                                          <Box>
                                            <Typography fontWeight="light">
                                              Min
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Box>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              <Box mt={10}  align="center">
                <Box display="flex" justifyContent="space-evenly">
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<Icon>article</Icon>}
                    onClick={() => {
                      // toPDF();
                      window.print();
                    }}
                  >
                    Save As PDF
                  </Button>
                  {campaign.access_type === "DOWNLOAD" && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Icon>download</Icon>}
                      onClick={() => {
                        const downloadUrl = `${AppConfig.api_base_url}campaign/window/report/${ref}`;
                        window.open(downloadUrl, "_blank");
                      }}
                    >
                      Full Report
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <CheckoutFooter />
    </Box>
  );
}

export default CampaignWindowPage;
